// React
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";

// Imagenes
import { BackgroundMain as BackgroudImage, LogoSplash } from "../../config/brandAssets";

import { useUserStore } from "../../store/userStore";
import {
  isTokenExpired,
  decodeJWT,
  clearAuthStorage,
  clearRoleStorage,
} from "../../utils/tokenUtils";

const apiUrl = import.meta.env.VITE_FIREBASE_API_URL;

export default function SplashScreen() {
  const setUser = useUserStore((s) => s.setUser);
  const navigate = useNavigate();
  const location = useLocation();
  const handledRef = useRef(false);

  useEffect(() => {
    // Solo se resuelve el destino una vez por montaje: evita que una segunda
    // pasada (re-render / cambio de pathname) consuma un lastRoute ya borrado
    // y rebote a /map.
    if (handledRef.current) return;
    handledRef.current = true;

    const token = localStorage.getItem("token");
    const goTo = (path: string) => {
      if (location.pathname !== path) {
        navigate(path);
      }
    };

    // Decide el destino una sola vez: lastRoute si existe, si no /map.
    const resolveTarget = () => {
      const lastRoute = localStorage.getItem("lastRoute");
      localStorage.removeItem("lastRoute");
      return lastRoute && lastRoute !== "/" && lastRoute !== "/splash"
        ? lastRoute
        : "/map";
    };

    const goToLogin = () => {
      setUser(null);
      clearAuthStorage();
      goTo("/");
    };

    const syncRoleStorage = async (freshUser: any, authToken: string) => {
      const roleId = freshUser?.role_id;
      if (!roleId) return clearRoleStorage();

      localStorage.setItem("userRoleId", roleId);
      try {
        const res = await fetch(
          `${apiUrl}/role?id=${encodeURIComponent(roleId)}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${authToken}` },
          },
        );
        const roleName = (await res.json())?.data?.data?.name;
        roleName
          ? localStorage.setItem("userRole", roleName)
          : localStorage.removeItem("userRole");
      } catch {
        localStorage.removeItem("userRole");
      }
    };

    if (token && !isTokenExpired(token)) {
      // Decodificar el token para obtener el email
      const decoded = decodeJWT(token);
      const email = decoded?.email;

      if (email) {
        fetch(
          `${apiUrl}/user?email=${encodeURIComponent(email)}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }
        )
          .then(async (res) => {
            const data = await res.json();
            if (data && (data.user || data.data)) {
              const freshUser = data.user || data.data;
              setUser(freshUser);
              await syncRoleStorage(freshUser, token);
              goTo(resolveTarget());
            } else {
              goToLogin();
            }
          })
          .catch(() => {
            goToLogin();
          });
      } else {
        // Token no tiene email, limpiar y ir al login
        goToLogin();
      }
    } else {
      // No hay token o está expirado
      goToLogin();
    }
  }, [location.pathname, navigate, setUser]);

  return (
    <div className="w-full h-screen flex flex-col relative">
      <img
        src={BackgroudImage}
        alt="Img not found"
        className="absolute w-full h-full object-cover z-[-1] brightness-50"
      />
      <div className="w-full h-screen flex flex-col items-center justify-center">
        <div className="py-8">
          <img
            src={LogoSplash}
            alt="Img not found"
            className="h-96 animate-pulse duration-700"
          />
        </div>
      </div>
    </div>
  );
}
